import type { BroadsideOrientation, BroadsidePlayer, CharacterSheet, HeroSeatSlot, PlayerBroadsideState, PlayerPokerState, PlayingCard, PokerChipThrowProfile, TableEdge } from "../types";

export const REMOTE_PROTOCOL_VERSION = 1;
export const REMOTE_MESSAGE_LIMIT = 64 * 1024;

export type RemoteCharacterSheet = Omit<CharacterSheet, "notes">;

export type RemoteGameSnapshot =
  | { game: "tabletop"; campaignName: string; heroId: string; heroName: string; heroColor: string; sheet: RemoteCharacterSheet; tokenMovement: boolean }
  | { game: "poker"; campaignName: string; seatId: string; seatName: string; seatColor: string; edge: TableEdge; slot: HeroSeatSlot; chips: number; committedChips: number; hand: PlayingCard[]; table: PlayerPokerState }
  | { game: "broadside"; campaignName: string; player: BroadsidePlayer; table: PlayerBroadsideState };

export type BrowserToRelayMessage =
  | { type: "host"; roomId: string; hostKey: string; protocol: number }
  | { type: "join"; roomId: string; clientId: string; name: string; protocol: number }
  | { type: "host-message"; clientId: string; payload: HostToRemotePayload }
  | { type: "remote-message"; payload: RemoteToHostPayload }
  | { type: "leave" };

export type RelayToBrowserMessage =
  | { type: "hosted"; roomId: string }
  | { type: "join-request"; clientId: string; name: string }
  | { type: "joined"; roomId: string }
  | { type: "remote-message"; clientId: string; payload: RemoteToHostPayload }
  | { type: "host-message"; payload: HostToRemotePayload }
  | { type: "peer-left"; clientId: string }
  | { type: "error"; message: string };

export type HostToRemotePayload =
  | { type: "pending" }
  | { type: "approved"; assignmentId: string; snapshot: RemoteGameSnapshot }
  | { type: "snapshot"; snapshot: RemoteGameSnapshot }
  | { type: "rejected"; message: string };

export type RemoteToHostPayload =
  | { type: "sheet-update"; sheet: RemoteCharacterSheet }
  | { type: "token-step"; dx: number; dy: number }
  | { type: "poker-play-card"; cardId: string; throwProfile?: PokerChipThrowProfile }
  | { type: "poker-play-cards"; cardIds: string[]; throwProfile?: PokerChipThrowProfile }
  | { type: "poker-reorder-hand"; cardIds: string[] }
  | { type: "poker-fold" }
  | { type: "poker-bet"; amount: number; throwProfile?: PokerChipThrowProfile }
  | { type: "broadside-place-ship"; shipId: string; x: number; y: number; orientation: BroadsideOrientation }
  | { type: "broadside-ready"; ready: boolean }
  | { type: "broadside-fire"; x: number; y: number }
  | { type: "ping" };

export function createRoomCode() {
  const data = new Uint32Array(1); crypto.getRandomValues(data); return String(data[0] % 1_000_000).padStart(6, "0");
}

export function createRemoteKey() {
  return Array.from(crypto.getRandomValues(new Uint8Array(24)), (value) => value.toString(16).padStart(2, "0")).join("");
}

export function normalizeRoomCode(value: string) {
  return value.replace(/\D/g, "").slice(0, 6);
}

export function remoteRelayUrl(locationLike: Pick<Location, "protocol" | "host"> = window.location) {
  if (/^(?:www\.)?tbl\.top(?::\d+)?$/i.test(locationLike.host)) return "wss://relay.tbl.top/relay";
  return `${locationLike.protocol === "https:" ? "wss:" : "ws:"}//${locationLike.host}/relay`;
}

export function playerSafeCharacterSheet(sheet: CharacterSheet): RemoteCharacterSheet {
  const { notes: _notes, ...safe } = sheet; return structuredClone(safe);
}

export function restoreRemoteCharacterSheet(remote: RemoteCharacterSheet, privateNotes: string): CharacterSheet {
  return { ...structuredClone(remote), notes: privateNotes, updatedAt: Date.now() };
}

export function isRemoteToHostPayload(value: unknown): value is RemoteToHostPayload {
  if (!value || typeof value !== "object" || !("type" in value)) return false;
  const payload = value as Record<string, unknown>;
  if (payload.type === "ping") return true;
  if (payload.type === "poker-fold") return true;
  if (payload.type === "token-step") return typeof payload.dx === "number" && Number.isFinite(payload.dx) && typeof payload.dy === "number" && Number.isFinite(payload.dy);
  if (payload.type === "poker-play-card") {
    if (typeof payload.cardId !== "string" || payload.cardId.length > 100) return false;
    if (payload.throwProfile === undefined) return true;
    if (!payload.throwProfile || typeof payload.throwProfile !== "object") return false;
    const profile = payload.throwProfile as Record<string, unknown>;
    return ["durationMs", "speed", "curve", "releaseX", "releaseY"].every((key) => typeof profile[key] === "number" && Number.isFinite(profile[key]));
  }
  if (payload.type === "poker-play-cards") {
    if (!Array.isArray(payload.cardIds) || payload.cardIds.length < 1 || payload.cardIds.length > 52 || payload.cardIds.some((cardId) => typeof cardId !== "string" || cardId.length < 1 || cardId.length > 100) || new Set(payload.cardIds).size !== payload.cardIds.length) return false;
    if (payload.throwProfile === undefined) return true;
    if (!payload.throwProfile || typeof payload.throwProfile !== "object") return false;
    const profile = payload.throwProfile as Record<string, unknown>;
    return ["durationMs", "speed", "curve", "releaseX", "releaseY"].every((key) => typeof profile[key] === "number" && Number.isFinite(profile[key]));
  }
  if (payload.type === "poker-reorder-hand") return Array.isArray(payload.cardIds) && payload.cardIds.length > 0 && payload.cardIds.length <= 52 && payload.cardIds.every((cardId) => typeof cardId === "string" && cardId.length > 0 && cardId.length <= 100) && new Set(payload.cardIds).size === payload.cardIds.length;
  if (payload.type === "poker-bet") {
    if (typeof payload.amount !== "number" || !Number.isFinite(payload.amount) || payload.amount <= 0) return false;
    if (payload.throwProfile === undefined) return true;
    if (!payload.throwProfile || typeof payload.throwProfile !== "object") return false;
    const profile = payload.throwProfile as Record<string, unknown>;
    return ["durationMs", "speed", "curve", "releaseX", "releaseY"].every((key) => typeof profile[key] === "number" && Number.isFinite(profile[key]));
  }
  if (payload.type === "broadside-place-ship") return typeof payload.shipId === "string" && payload.shipId.length > 0 && payload.shipId.length <= 40 && typeof payload.x === "number" && Number.isInteger(payload.x) && typeof payload.y === "number" && Number.isInteger(payload.y) && payload.x >= 0 && payload.x < 100 && payload.y >= 0 && payload.y < 100 && (payload.orientation === "horizontal" || payload.orientation === "vertical");
  if (payload.type === "broadside-ready") return typeof payload.ready === "boolean";
  if (payload.type === "broadside-fire") return typeof payload.x === "number" && Number.isInteger(payload.x) && typeof payload.y === "number" && Number.isInteger(payload.y) && payload.x >= 0 && payload.x < 100 && payload.y >= 0 && payload.y < 100;
  if (payload.type !== "sheet-update" || !payload.sheet || typeof payload.sheet !== "object") return false;
  const sheet = payload.sheet as Partial<RemoteCharacterSheet>;
  return typeof sheet.id === "string" && typeof sheet.name === "string" && typeof sheet.profileId === "string" && Array.isArray(sheet.stats) && sheet.stats.every((stat) => Boolean(stat) && typeof stat === "object" && typeof stat.id === "string" && typeof stat.label === "string" && typeof stat.value === "number" && Number.isFinite(stat.value)) && Array.isArray(sheet.tracks) && sheet.tracks.every((track) => Boolean(track) && typeof track === "object" && typeof track.id === "string" && typeof track.label === "string" && typeof track.current === "number" && Number.isFinite(track.current) && typeof track.max === "number" && Number.isFinite(track.max));
}
